import { getAdminKey, AdminKeyRejected } from './adminKey';

// All admin endpoints now live on the Node backend (the previous FastAPI
// service at web-api.valetnyc.co was retired). Node mounts admin routes at
// /api/admin so we use that prefix everywhere.
const NODE_API = process.env.REACT_APP_NODE_API_URL || 'https://api.valetnetwork.co';
const BASE_URL = `${NODE_API}/api`;

// Poster scan counts are the one thing that does NOT live on the Node backend.
// The street posters point at the marketing site (valetnetwork.co/park?src=…),
// so that site is what counts the scans and that site is what serves them back.
// Its /api/scan GET sends Access-Control-Allow-Origin so this cross-origin read
// works from the dashboard's own domain.
const MARKETING_SITE =
    process.env.REACT_APP_MARKETING_URL || 'https://valetnetwork.co';

// Every /api/admin route is behind an `x-admin-key` header. It used to gate
// only the notification broadcast routes, which left the customer list — names
// and phone numbers — readable by anyone who knew the URL. The key is typed in
// by the operator at runtime and held in sessionStorage, so it never lands in
// the build output.
//
// `key` is optional: the Notifications tab passes the key it is validating
// before that key has been stored. Everything else reads the stored one.
const adminFetch = async (path, options = {}, key) => {
    const adminKey = key || getAdminKey();
    const res = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'x-admin-key': adminKey,
            ...(options.headers || {}),
        },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
        // The API's own word for this is "Unauthorized", which tells the
        // operator nothing about what to do. Use the plainer sentence.
        if (res.status === 401) throw new AdminKeyRejected();
        throw new Error(payload?.message || `Request failed (${res.status})`);
    }
    return payload;
};

// Cheap probe used by the unlock screen — passes the gate and returns nothing
// else, so it is safe to call on every page load.
export const verifyAdminKey = (key) => adminFetch('/admin/session', {}, key);

function getDateRange(period) {
  const now = new Date();
  const end = now.toISOString();
  const start = new Date(now);
  if (period === 'Today') start.setHours(0, 0, 0, 0);
  else if (period === '7 days') start.setDate(now.getDate() - 7);
  else if (period === '30 days') start.setDate(now.getDate() - 30);
  else if (period === '90 days') start.setDate(now.getDate() - 90);
  return { startDate: start.toISOString(), endDate: end };
}

function fetchMetrics(endpoint, period) {
  const { startDate, endDate } = getDateRange(period);
  const params = new URLSearchParams({ startDate, endDate });
  return adminFetch(`/admin/metrics/${endpoint}?${params}`);
}

export const getBusinessMetrics = (period) => fetchMetrics('business', period);
export const getUserMetrics     = (period) => fetchMetrics('users', period);
export const getValetMetrics    = (period) => fetchMetrics('valets', period);
export const getValets          = () => adminFetch('/admin/valets');
export const getUsers           = () => adminFetch('/admin/users');

// Every service request ever created (Services tab). Newest first, with the
// customer's name/phone and the assigned valet already joined in by the API.
export const getServices        = () => adminFetch('/admin/services');

// Every subscription ever bought (Subscribers tab), with the customer joined
// in and the roll-ups — MRR, tier split, covered orders, suspension credits —
// already computed. One call: the tab's filters all run over the same rows.
export const getSubscriptions   = () => adminFetch('/admin/subscriptions');

// How many people scanned each street poster. Returns
// { ok, total, scans: [{ src, count, lastAt, lastPath, byDay }] }, busiest first.
// `byDay` is keyed YYYY-MM-DD in New York time, not UTC.
//
// Deliberately a plain fetch: this is the marketing site, not the admin API, and
// sending it a custom header would force a preflight it does not answer.
export const getPosterScans = async () => {
    const res = await fetch(`${MARKETING_SITE}/api/scan`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`Failed to load poster scans (${res.status})`);
    const body = await res.json();
    if (!body || body.ok !== true) throw new Error('Poster scan counts unavailable');
    return body;
};

/**
 * Visits to the two decks — `/deck` (investors) and `/amenity` (buildings).
 * Same public counter as the posters above, different collection; see the
 * marketing site's `/api/views`. `?json` forces JSON, because a browser
 * hitting that URL gets an HTML page instead.
 */
export const getDeckViews = async () => {
    const res = await fetch(`${MARKETING_SITE}/api/views?json=1`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`Failed to load deck visits (${res.status})`);
    const body = await res.json();
    if (!body || body.ok !== true) throw new Error('Deck visit counts unavailable');
    return body;
};

/**
 * The full record of every visit to a deck — or to one labelled link of it —
 * slide by slide, with the device, the city and a timeline of what the reader
 * did. Served by the marketing site's private report, which accepts this
 * console's admin key (it checks it with the backend's /admin/session), so the
 * report's own key never has to live in this build.
 *
 * Not routed through adminFetch: that turns a refusal into AdminKeyRejected,
 * which locks the whole console, and a hiccup on the marketing site should not
 * log the operator out. A refusal here is just an error in this one card.
 */
export const getDeckReport = async ({ page = 'deck', src } = {}) => {
    const q = new URLSearchParams({ page, json: '1', ...(src ? { src } : {}) });
    const res = await fetch(`${MARKETING_SITE}/api/views/report?${q.toString()}`, {
        cache: 'no-store',
        headers: { 'x-admin-key': getAdminKey() },
    });
    if (res.status === 404) {
        throw new Error('The visit report did not accept this console key. Lock and unlock the console, then refresh.');
    }
    if (!res.ok) throw new Error(`Failed to load the visit report (${res.status})`);
    const body = await res.json();
    if (!body || body.ok !== true) throw new Error('Visit report unavailable');
    return body;
};

const postValetAction = (path, valetId) =>
  adminFetch(path, { method: 'POST', body: JSON.stringify({ valetId }) });

export const deactivateValet    = (id) => postValetAction('/admin/valets/deactivate', id);
export const reactivateValet    = (id) => postValetAction('/admin/valets/reactivate', id);

// Admin override — flip a valet to `active` from any non-active state.
// Used to unstick valets caught in the v1.2.0 onboarding gate (pending_certn,
// pending_documents, pending_provider_approval, certn_failed, suspended_admin).
export const forceActivateValet = (userId, reason) =>
    adminFetch(`/admin/valets/onboarding/${userId}/force-activate`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
    });

// Normal-path approve — only valid when valet is in `pending_provider_approval`.
// Moves them to `active`.
export const authorizeValet = (userId) =>
    adminFetch(`/admin/valets/onboarding/${userId}/authorize`, {
        method: 'POST',
        body: JSON.stringify({}),
    });

// Reject path — moves the valet to `suspended_admin`. They can be reinstated
// later via the existing reactivateValet action.
export const suspendValet = (userId, reason) =>
    adminFetch(`/admin/valets/onboarding/${userId}/suspend`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
    });

// ===== Payouts =====

export const getPayouts = (status = 'requested') => {
    const params = new URLSearchParams({ status });
    return adminFetch(`/admin/payouts?${params}`);
};

export const markPayoutPaid = (payoutId, adminNote) =>
    adminFetch(`/admin/payouts/${payoutId}/markPaid`, {
        method: 'POST',
        body: JSON.stringify(adminNote ? { adminNote } : {}),
    });

// ===== Parking photos (Photos tab) =====

export const getParkingPhotos = ({ limit = 50, skip = 0 } = {}) => {
    const params = new URLSearchParams({ limit, skip });
    return adminFetch(`/admin/parking-photos?${params}`);
};

export const deleteParkingPhoto = (photoId) =>
    adminFetch(`/admin/parking-photo/${photoId}`, { method: 'DELETE' });

export const getParkingRulePhotos = ({ limit = 50, skip = 0 } = {}) => {
    const params = new URLSearchParams({ limit, skip });
    return adminFetch(`/admin/parking-rule-photos?${params}`);
};

export const deleteParkingRulePhoto = (id) =>
    adminFetch(`/admin/parking-rule-photo/${id}`, { method: 'DELETE' });

// ===== Pricing (Pricing tab) =====
// Lives at /api/pricing (not /api/admin). The GET is genuinely public — the
// mobile app and the web checkout both read it on launch to quote a price — so
// it stays a plain fetch.
//
// The PUT is not. The backend now charges from these values instead of
// trusting the amount the app posts, so an open write here set the price of
// every order in the system. It is gated on the same x-admin-key as /api/admin
// and goes through adminFetch, which means a rejected key raises
// AdminKeyRejected and the unlock screen reappears, exactly as on other tabs.

export const getPricing = async () => {
    const res = await fetch(`${BASE_URL}/pricing`);
    if (!res.ok) throw new Error(`Failed to fetch pricing (${res.status})`);
    return res.json();
};

export const updatePricing = (payload) =>
    adminFetch('/pricing', {
        method: 'PUT',
        body: JSON.stringify(payload),
    });

// --- Push notifications (Notifications tab) ---
// These take an explicit key because the tab validates a freshly typed one
// before storing it.

export const getAudienceCounts = (key) =>
    adminFetch('/admin/notifications/audience-counts', {}, key);

export const getNotificationHistory = (key, limit = 50) =>
    adminFetch(`/admin/notifications/history?limit=${limit}`, {}, key);

export const sendAdminNotification = (key, payload) =>
    adminFetch('/admin/notifications/send', {
        method: 'POST',
        body: JSON.stringify(payload),
    }, key);

export const runParkingAlertsNow = (key) =>
    adminFetch('/admin/notifications/run-parking-alerts', { method: 'POST' }, key);
